import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiTarget, FiZap, FiVideo, FiPenTool, FiMonitor, FiBook, FiDollarSign, FiTrendingUp, FiHome, FiUsers, FiStar } = FiIcons;

const Services = () => {
  const services = [
    {
      title: 'Strategy & Advisory',
      summary: 'Vision clarity, strategic planning, and founder-level guidance for meaningful work.',
      path: '/services/strategy-advisory',
      icon: FiTarget
    },
    {
      title: 'AI & Automation',
      summary: 'Practical AI implementation, workflows, and agent-based systems for leverage.',
      path: '/services/ai-automation',
      icon: FiZap
    },
    {
      title: 'Video & Media Production',
      summary: 'Storytelling infrastructure through video production and creative direction.',
      path: '/services/video-media',
      icon: FiVideo
    },
    {
      title: 'Creative Consulting',
      summary: 'Brand clarity, narrative development, and creative direction for campaigns.',
      path: '/services/creative-consulting',
      icon: FiPenTool
    },
    {
      title: 'Digital Products & Web',
      summary: 'Platform architecture, product ideation, and web experiences that last.',
      path: '/services/digital-products',
      icon: FiMonitor
    },
    {
      title: 'Crypto Education',
      summary: 'Blockchain literacy, security education, and calm confidence without hype.',
      path: '/services/crypto-education',
      icon: FiBook
    },
    {
      title: 'Crypto Consulting',
      summary: 'Strategic infrastructure focus for tokens, communities, and web3 products.',
      path: '/services/crypto-consulting',
      icon: FiDollarSign
    },
    {
      title: 'Investments & Ventures',
      summary: 'Selective participation in ventures where alignment exists and value can be created.',
      path: '/services/investments-ventures',
      icon: FiTrendingUp
    },
    {
      title: 'Property Management',
      summary: 'Asset oversight, operational systems, and stewardship for select properties.',
      path: '/services/property-management',
      icon: FiHome
    },
    {
      title: 'Community & Events',
      summary: 'Educational gatherings, community building, and collaborative growth initiatives.',
      path: '/services/community-events',
      icon: FiUsers
    },
    {
      title: 'Special Projects',
      summary: 'Cross-disciplinary experiments, incubation, and undefined but meaningful work.',
      path: '/services/special-projects',
      icon: FiStar
    }
  ];

  const engagementModes = [
    'Advisory',
    'Operator Support',
    'Project-Based Execution',
    'Ongoing Stewardship',
    'Investment Participation (Select)'
  ];

  const processSteps = [
    'Clarity Call',
    'Scope Alignment',
    'Proposal or Next Steps',
    'Kickoff or Referral'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Services</h1>
            <p className="text-xl text-slate-300 mb-8">
              A map of our disciplines, not a menu of fixed packages.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book"
                className="bg-white text-slate-900 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-colors inline-flex items-center justify-center"
              >
                Book a Call
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/process"
                className="border border-slate-400 text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-800 transition-colors"
              >
                See Our Process
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={service.path}
                  className="block bg-slate-50 p-6 rounded-xl hover:bg-slate-100 transition-all hover:shadow-lg group h-full"
                >
                  <SafeIcon
                    icon={service.icon}
                    className="w-10 h-10 text-slate-700 mb-4 group-hover:text-slate-900 transition-colors"
                  />
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600 mb-4 leading-relaxed">{service.summary}</p>
                  <div className="flex items-center text-slate-700 group-hover:text-slate-900 transition-colors">
                    <span className="text-sm font-medium">Learn more</span>
                    <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-slate-900 mb-6">How We Work</h2>
              <p className="text-lg text-slate-600 mb-8">
                Our engagement modes adapt to what serves the work best. We shift between advisory, operational support, and direct execution based on alignment and opportunity.
              </p>
              <div className="space-y-3">
                {engagementModes.map((mode, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-slate-800 rounded-full"></div>
                    <span className="text-slate-700 font-medium">{mode}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h3 className="text-2xl font-semibold text-slate-900 mb-6">What to Expect</h3>
              <div className="space-y-6">
                {processSteps.map((step, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-slate-800 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{step}</h4>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Who It's For</h2>
            <p className="text-xl text-slate-600 mb-8">
              We work with people and projects that value integrity, alignment, and thoughtful execution.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-lg">
              {['Founders', 'Owners', 'Partners', 'Families', 'Creators', 'Operators'].map((type, index) => (
                <span key={index} className="bg-slate-100 text-slate-800 px-4 py-2 rounded-lg font-medium">
                  {type}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-6">Ready to Begin?</h2>
            <p className="text-xl text-slate-300 mb-8">
              Every engagement begins with conversation and alignment. From there, we determine the appropriate role and structure.
            </p>
            <Link
              to="/book"
              className="bg-white text-slate-900 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-colors inline-flex items-center"
            >
              Book a Call
              <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;